/**
 * AccountSetupContext
 *
 * Manages account setup journey state and provides functionality to track
 * user progress through the onboarding stages. This context handles:
 * - Account creation status
 * - Email verification status
 * - Phone verification status
 * - Payment method status (required before paid subscriptions)
 * - Subscription status
 * - User addition capability
 * - Simulation profile setup
 * - Scam checking completion
 *
 * @module AccountSetupContext
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useUser } from "./UserContext";
import { useSubscription } from "./SubscriptionContext";
import { getUserRoles } from "../../../utils/jwtUtils";
import UserAPI from "../services/UserAPI";
import StripePaymentAPI from "../services/StripePaymentAPI";
import UserSettingsAPI from "../services/UserSettingsAPI";
import SubscriptionsAPI from "../services/SubscriptionsAPI";
import SimulationProfileAPI from "../services/SimulationProfileAPI";

// Create the AccountSetupContext
const AccountSetupContext = createContext();

/**
 * Custom hook to use the AccountSetupContext
 * @throws {Error} If used outside of AccountSetupProvider
 * @returns {Object} Account setup context value
 */
export const useAccountSetup = () => {
  const context = useContext(AccountSetupContext);
  if (!context) {
    throw new Error(
      "useAccountSetup must be used within an AccountSetupProvider",
    );
  }
  return context;
};

/**
 * AccountSetupProvider component to wrap the app and provide account setup state
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AccountSetupProvider = ({ children }) => {
  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  const { loggedInUser } = useUser();
  const { hasActiveSubscription, servicePlan, subscription } =
    useSubscription();

  const [setupProgress, setSetupProgress] = useState({
    accountCreated: false,
    emailVerified: false,
    phoneVerified: false,
    hasCheckoutDraft: false,
    hasSubscription: false,
    hasPaymentMethod: false,
    canAddUsers: false,
    usersAdded: "not-started", // 'not-started', 'partial', or 'complete'
    simulationProfileSetup: false,
    scamCheckingComplete: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isAccountOwner, setIsAccountOwner] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Track whether Dashboard should show only the Setup Journey panel
  // (true when account owner AND progress <= 90%)
  const [setupJourneyOnly, setSetupJourneyOnly] = useState(false);

  // Ref to track previous progress percentage for threshold-crossing detection
  const prevProgressRef = useRef(null);

  // =========================================================================
  // LOAD SETUP PROGRESS
  // =========================================================================

  useEffect(() => {
    const loadSetupProgress = async () => {
      console.log("🔄 AccountSetupContext: loadSetupProgress called");
      console.log("👤 AccountSetupContext: loggedInUser:", loggedInUser);

      if (!loggedInUser) {
        console.log(
          "⚠️ AccountSetupContext: No logged in user, setting isLoading to false",
        );
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log("🔍 AccountSetupContext: Checking account ownership...");

        // Check if user is account owner
        let isOwner = false;
        try {
          const ownershipResponse = await UserAPI.getUserAccountOwnership();
          console.log(
            "📋 AccountSetupContext: Ownership response:",
            ownershipResponse,
          );
          isOwner = ownershipResponse?.isAccountOwner || false;
        } catch (ownershipError) {
          // If 403 or any error, user is not an account owner
          console.log(
            "⚠️ AccountSetupContext: Error checking ownership (likely not account owner):",
            ownershipError.message || ownershipError,
          );
          isOwner = false;
        }

        console.log("✅ AccountSetupContext: isAccountOwner:", isOwner);
        setIsAccountOwner(isOwner);

        // If not account owner, don't show setup journey
        if (!isOwner) {
          setIsLoading(false);
          return;
        }

        // Account is created if user is logged in
        const accountCreated = true;

        // Check email and phone verification
        const emailVerified = loggedInUser?.emailVerified || false;
        const phoneVerified = loggedInUser?.phoneVerified || false;

        // Check subscription status
        const hasSubscription = hasActiveSubscription;

        // Check whether user has a saved checkout draft (selected plan, not yet subscribed)
        let hasCheckoutDraft = false;
        try {
          const checkoutDraftResponse =
            await SubscriptionsAPI.getCheckoutDraft();
          hasCheckoutDraft =
            checkoutDraftResponse?.success &&
            (checkoutDraftResponse?.hasDraft ||
              Boolean(checkoutDraftResponse?.data?.planId));
          console.log(
            "🧾 AccountSetupContext: has checkout draft:",
            hasCheckoutDraft,
          );
        } catch (draftError) {
          console.warn(
            "⚠️ AccountSetupContext: Could not load checkout draft state:",
            draftError,
          );
          hasCheckoutDraft = false;
        }

        // Demo seat accounts are comped — no Stripe payment method exists.
        // Use the same getUserRoles() path as useRoleCheck/isDemoSeat so that
        // the server-synced, event-driven role value is the source of truth.
        const allRoles = getUserRoles();
        const isDemoSeatUser = allRoles.includes("demo_seat");

        const isTrialSubscription = Boolean(subscription?.isTrial);

        // Check if user has at least one payment method set as default
        let hasPaymentMethod = false;
        if (isDemoSeatUser) {
          // Demo seats use a comped plan; no payment method needed.
          hasPaymentMethod = false;
          console.log(
            "🎭 AccountSetupContext: Demo seat detected — skipping payment method check (treated as complete)",
          );
        } else if (isTrialSubscription) {
          // Trial subscriptions can use app functionality before adding a card.
          hasPaymentMethod = false;
          console.log(
            "🧪 AccountSetupContext: Trial subscription detected — payment method is not required for setup progress",
          );
        } else {
          try {
            const paymentMethods = await StripePaymentAPI.getPaymentMethods();
            console.log(
              "💳 AccountSetupContext: Payment methods response:",
              paymentMethods,
            );
            console.log(
              "💳 AccountSetupContext: Is array?",
              Array.isArray(paymentMethods),
            );
            console.log(
              "💳 AccountSetupContext: Payment methods length:",
              paymentMethods?.length,
            );

            // Check if there's at least one default payment method
            hasPaymentMethod =
              Array.isArray(paymentMethods) &&
              paymentMethods.length > 0 &&
              paymentMethods.some((pm) => pm.isDefault === true);
            console.log(
              "✅ AccountSetupContext: Has default payment method:",
              hasPaymentMethod,
            );
          } catch (error) {
            console.error(
              "❌ AccountSetupContext: Error checking payment methods:",
              error,
            );
            hasPaymentMethod = false;
          }
        }

        // Determine if user can add more users based on service plan
        const maxUsers = servicePlan?.maxUsers || 1;
        const canAddUsers = maxUsers > 1;

        // Check how many users have been added
        let usersAdded = "not-started";
        if (maxUsers === 1) {
          // If plan only allows 1 user, this step doesn't apply — mark as not-started (shows as optional)
          usersAdded = "not-started";
        } else if (loggedInUser) {
          // SECURITY: Use context-based endpoint - no groupId required
          // Only call if user has required roles (group_member, group_admin, account_admin, platform_admin)
          const allowedRoles = [
            "group_member",
            "group_admin",
            "account_admin",
            "platform_admin",
          ];
          const userRoles = getUserRoles();
          const hasGroupAccess = userRoles.some((role) =>
            allowedRoles.includes(role),
          );

          if (hasGroupAccess) {
            // If plan allows multiple users, check if any member users exist
            try {
              const userCountResponse = await UserAPI.getUserCountInMyGroup();
              // Handle different response formats (number or object with count property)
              const userCount =
                typeof userCountResponse === "number"
                  ? userCountResponse
                  : userCountResponse?.count ||
                    userCountResponse?.data?.count ||
                    userCountResponse?.data ||
                    0;
              console.log(
                "👥 AccountSetupContext: User count response:",
                userCountResponse,
              );
              console.log(
                "👥 AccountSetupContext: Parsed user count:",
                userCount,
              );
              if (userCount > 1) {
                // At least one additional member user has been added
                usersAdded = "complete";
              } else {
                // No additional users yet, but subscription allows them
                usersAdded = "partial";
              }
            } catch (error) {
              console.error(
                "❌ AccountSetupContext: Error getting user count:",
                error,
              );
              // Default to partial if we can add users but count check failed
              usersAdded = "partial";
            }
          } else {
            console.log(
              "👥 AccountSetupContext: User does not have group access roles, skipping group count fetch",
            );
            // Non-admin users don't need to track user count for setup progress
            usersAdded = "complete";
          }
        }

        // Check simulation profile setup - verify a profile actually exists
        let simulationProfileSetup = false;
        if (hasSubscription && loggedInUser?.uuid) {
          try {
            const profile =
              await SimulationProfileAPI.getSimulationProfilesByUuid(
                loggedInUser.uuid,
              );
            simulationProfileSetup = !!profile;
          } catch (err) {
            // 404 means no profile exists yet
            simulationProfileSetup = false;
          }
        }

        // Check scam checking completion (from localStorage or API)
        const scamCheckingComplete =
          localStorage.getItem("scamCheckingComplete") === "true" || false;

        const progress = {
          accountCreated,
          emailVerified,
          phoneVerified,
          hasCheckoutDraft,
          hasSubscription,
          hasPaymentMethod,
          canAddUsers,
          usersAdded,
          simulationProfileSetup,
          scamCheckingComplete,
        };
        console.log("📊 AccountSetupContext: Final setup progress:", progress);
        setSetupProgress(progress);
      } catch (error) {
        console.error(
          "❌ AccountSetupContext: Error loading account setup progress:",
          error,
        );
      } finally {
        console.log("🏁 AccountSetupContext: Setting isLoading to false");
        setIsLoading(false);
      }
    };

    loadSetupProgress();
  }, [
    loggedInUser,
    hasActiveSubscription,
    servicePlan,
    subscription,
    refreshTrigger,
  ]);

  useEffect(() => {
    const handleCheckoutDraftUpdated = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    window.addEventListener("checkoutDraftUpdated", handleCheckoutDraftUpdated);

    return () => {
      window.removeEventListener(
        "checkoutDraftUpdated",
        handleCheckoutDraftUpdated,
      );
    };
  }, []);

  // =========================================================================
  // HELPER FUNCTIONS
  // =========================================================================

  /**
   * Mark a setup stage as complete
   * @param {string} stage - The stage to mark as complete
   */
  const markStageComplete = (stage) => {
    setSetupProgress((prev) => ({
      ...prev,
      [stage]: true,
    }));

    // Persist certain stages to localStorage
    if (
      stage === "simulationProfileSetup" ||
      stage === "scamCheckingComplete"
    ) {
      localStorage.setItem(stage, "true");
    }
  };

  /**
   * Refresh the setup progress
   */
  const refreshSetupProgress = async () => {
    console.log("🔄 AccountSetupContext: Triggering refresh...");
    setRefreshTrigger((prev) => prev + 1);
  };

  /**
   * Calculate overall progress percentage
   */
  const getProgressPercentage = () => {
    const stages = Object.entries(setupProgress);
    let totalWeight = 0;
    let completedWeight = 0;
    const isTrialSubscription = Boolean(subscription?.isTrial);
    const isPaymentMethodRequired = !isTrialSubscription;

    stages.forEach(([key, value]) => {
      // Skip canAddUsers as it's a capability flag, not a stage
      // Skip scamCheckingComplete as it's an optional stage
      if (key === "canAddUsers" || key === "scamCheckingComplete") return;
      if (key === "hasPaymentMethod" && !isPaymentMethodRequired) return;

      if (key === "usersAdded") {
        if (value === "not-started") {
          // Single-user plan — step doesn't apply, exclude from total
          return;
        }
        totalWeight += 1;
        if (value === "complete") {
          completedWeight += 1;
        } else if (value === "partial") {
          completedWeight += 0.5;
        }
      } else if (key === "hasCheckoutDraft") {
        totalWeight += 1;
        if (value === true || setupProgress.hasSubscription === true) {
          completedWeight += 1;
        }
      } else {
        totalWeight += 1;
        if (value === true) {
          completedWeight += 1;
        }
      }
    });

    return Math.round((completedWeight / totalWeight) * 100);
  };

  /**
   * Check if all setup stages are complete
   */
  const isSetupComplete = () => {
    const isTrialSubscription = Boolean(subscription?.isTrial);
    const isPaymentMethodRequired = !isTrialSubscription;

    return Object.entries(setupProgress).every(([key, value]) => {
      if (key === "canAddUsers") return true; // Skip capability flag
      if (key === "scamCheckingComplete") return true; // Skip optional stage
      if (key === "hasPaymentMethod" && !isPaymentMethodRequired) return true; // Trial: payment method not required
      if (key === "usersAdded")
        return value === "complete" || value === "not-started"; // not-started = single-user plan, doesn't apply
      if (key === "hasCheckoutDraft") {
        return value === true || setupProgress.hasSubscription === true;
      }
      return value === true;
    });
  };

  // =========================================================================
  // SETUP JOURNEY ONLY MODE & THRESHOLD TRANSITION
  // =========================================================================

  // Update setupJourneyOnly whenever progress or ownership changes
  useEffect(() => {
    if (isLoading) return;

    const currentProgress = getProgressPercentage();

    if (isAccountOwner) {
      // Force setup-journey-only mode when phone is not yet verified.
      // This ensures Dashboard shows only the Account Setup Progress panel
      // so the user is guided to complete phone verification first.
      if (!loggedInUser?.phoneVerified) {
        setSetupJourneyOnly(true);
        prevProgressRef.current = currentProgress;
        return;
      }

      // Determine if we should show only the setup journey
      setSetupJourneyOnly(currentProgress <= 90);

      // Detect threshold crossing: <=90% → >90%
      if (
        prevProgressRef.current !== null &&
        prevProgressRef.current <= 90 &&
        currentProgress > 90
      ) {
        console.log(
          "🎯 AccountSetupContext: Progress crossed 90% threshold (",
          prevProgressRef.current,
          "→",
          currentProgress,
          "). Switching landing page to Getting Started.",
        );

        // Immediately show all Dashboard panels (setupJourneyOnly → false)
        setSetupJourneyOnly(false);

        // Persist landing page change for next login
        localStorage.setItem("landingPage", "Getting Started");
        UserSettingsAPI.updateSettings({
          defaultLandingPage: "Getting Started",
        }).catch((err) => {
          console.error(
            "❌ AccountSetupContext: Failed to update landing page setting:",
            err,
          );
        });
      }

      // Track previous progress for next comparison
      prevProgressRef.current = currentProgress;
    } else {
      // Non-account-owners always see the full dashboard
      setSetupJourneyOnly(false);
    }
  }, [setupProgress, isLoading, isAccountOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // CONTEXT VALUE
  // =========================================================================

  const value = {
    setupProgress,
    isLoading,
    isAccountOwner,
    setupJourneyOnly,
    markStageComplete,
    refreshSetupProgress,
    getProgressPercentage,
    isSetupComplete,
  };

  return (
    <AccountSetupContext.Provider value={value}>
      {children}
    </AccountSetupContext.Provider>
  );
};

export default AccountSetupContext;
